import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AudioConstraints } from '../../../shared/types/index.js';

interface UseAudioProcessorOptions {
  volumeThreshold?: number;
  sampleRate?: number;
}

interface AudioProcessorState {
  isCapturing: boolean;
  currentPitch: number | null;
  currentVolume: number;
  error: string | null;
  permissionGranted: boolean;
  permissionDenied: boolean;
  calibrationRange: { min: number; max: number } | null;
}

export function useAudioProcessor(options: UseAudioProcessorOptions = {}) {
  const {
    volumeThreshold = 0.01,
    sampleRate = 44100
  } = options;

  const [state, setState] = useState<AudioProcessorState>({
    isCapturing: false,
    currentPitch: null,
    currentVolume: 0,
    error: null,
    permissionGranted: false,
    permissionDenied: false,
    calibrationRange: null
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Pitch detection state
  const bufferRef = useRef<Float32Array | null>(null);
  const pitchHistoryRef = useRef<number[]>([]);
  const calibrationSamplesRef = useRef<number[]>([]);

  // Move audioConstraints inside useMemo to prevent re-creation on every render
  const audioConstraints = React.useMemo((): AudioConstraints => ({
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate
  }), [sampleRate]);

  // Autocorrelation algorithm for pitch detection
  const detectPitch = useCallback((buffer: Float32Array, sampleRate: number): number | null => {
    const bufferSize = buffer.length;
    const minPeriod = Math.floor(sampleRate / 800); // ~800Hz max
    const maxPeriod = Math.floor(sampleRate / 80);  // ~80Hz min

    let bestCorrelation = 0;
    let bestPeriod = 0;

    // Autocorrelation
    for (let period = minPeriod; period < maxPeriod && period < bufferSize / 2; period++) {
      let correlation = 0;
      for (let i = 0; i < bufferSize - period; i++) {
        correlation += buffer[i] * buffer[i + period];
      }

      if (correlation > bestCorrelation) {
        bestCorrelation = correlation;
        bestPeriod = period;
      }
    }

    // Check if correlation is strong enough
    if (bestCorrelation < 0.01 || bestPeriod === 0) {
      return null;
    }

    return sampleRate / bestPeriod;
  }, []);

  // Calculate RMS volume
  const calculateVolume = useCallback((buffer: Float32Array): number => {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    return Math.sqrt(sum / buffer.length);
  }, []);

  // No pitch smoothing - use raw detected pitch for maximum responsiveness

  // Audio processing loop
  const processAudio = useCallback(() => {
    if (!analyserRef.current || !bufferRef.current) {
      return;
    }

    analyserRef.current.getFloatTimeDomainData(bufferRef.current);

    const volume = calculateVolume(bufferRef.current);

    let pitch: number | null = null;

    // Only detect pitch if volume is above threshold
    if (volume > volumeThreshold) {
      const detectedPitch = detectPitch(bufferRef.current, sampleRate);
      if (detectedPitch) {
        pitch = detectedPitch; // Use raw pitch - no smoothing

        // Add to calibration samples
        calibrationSamplesRef.current.push(pitch);
        if (calibrationSamplesRef.current.length > 100) {
          calibrationSamplesRef.current.shift();
        }

        // Update calibration range
        if (calibrationSamplesRef.current.length > 10) {
          const samples = calibrationSamplesRef.current;
          const min = Math.min(...samples);
          const max = Math.max(...samples);

          setState(prev => ({
            ...prev,
            calibrationRange: { min, max }
          }));
        }
      }
    }

    setState(prev => ({
      ...prev,
      currentPitch: pitch,
      currentVolume: volume
    }));

    animationFrameRef.current = requestAnimationFrame(processAudio);
  }, [volumeThreshold, sampleRate, detectPitch, calculateVolume]);

  const startCapture = useCallback(async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, error: null, permissionDenied: false }));

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Microphone access is not supported in this browser');
      }

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: audioConstraints.echoCancellation,
          noiseSuppression: audioConstraints.noiseSuppression,
          autoGainControl: audioConstraints.autoGainControl,
          sampleRate: audioConstraints.sampleRate
        }
      });

      streamRef.current = stream;

      // Create audio context
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Create analyser node
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 4096;
      analyserRef.current.smoothingTimeConstant = 0.1; // No smoothing for maximum responsiveness
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;

      // Create microphone source
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      microphoneRef.current.connect(analyserRef.current);

      // Create buffer for audio data
      bufferRef.current = new Float32Array(analyserRef.current.fftSize);

      setState(prev => ({
        ...prev,
        isCapturing: true,
        permissionGranted: true,
        permissionDenied: false
      }));

      // Start processing loop
      processAudio();

    } catch (error) {
      console.error('Error starting audio capture:', error);
      
      let errorMessage = 'Failed to access microphone';
      let permissionDenied = false;
      
      if (error instanceof Error) {
        // Handle specific error types
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
          permissionDenied = true;
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          errorMessage = 'Microphone is already in use by another application.';
        } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
          errorMessage = 'Microphone does not support the required audio settings.';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Microphone access is not supported in this browser.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setState(prev => ({
        ...prev,
        error: errorMessage,
        permissionGranted: false,
        permissionDenied,
        isCapturing: false
      }));
    }
  }, [audioConstraints, processAudio]);

  const stopCapture = useCallback((): void => {
    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Disconnect audio nodes
    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    bufferRef.current = null;
    pitchHistoryRef.current = [];

    setState(prev => ({
      ...prev,
      isCapturing: false,
      currentPitch: null,
      currentVolume: 0
    }));
  }, []);

  const calibrate = useCallback((): void => {
    calibrationSamplesRef.current = [];
    setState(prev => ({
      ...prev,
      calibrationRange: null
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCapture();
    };
  }, [stopCapture]);

  return {
    ...state,
    startCapture,
    stopCapture,
    calibrate
  };
}